import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './AdminDashboard.css'
import '../styles/dashboard-header.css'
import LoadingScreen from '../components/LoadingScreen'

const AdminDashboard = () => {
  const [user, setUser] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notificationClicked, setNotificationClicked] = useState(false)
  const history = useHistory()

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      history.push('/login')
      return
    }

    const userData = JSON.parse(localStorage.getItem('user') || '{}')
    if (userData.role !== 'admin') {
      // Redirect to appropriate dashboard
      if (userData.role === 'teacher') {
        history.push('/teacher-dashboard')
      } else if (userData.role === 'invigilator') {
        history.push('/invigilator-dashboard')
      } else if (userData.role === 'volunteer') {
        history.push('/volunteer-dashboard')
      } else {
        history.push('/student-dashboard')
      }
      return
    }

    setUser(userData)
    fetchDashboardData()
  }, [history])
  
  useEffect(() => {
    // Check notification state when dashboard data is loaded
    if (dashboardData?.statistics?.total_pending_approvals !== undefined) {
      const lastPendingCount = localStorage.getItem('last_pending_approvals_count')
      const currentPendingCount = dashboardData.statistics.total_pending_approvals
      
      // If count increased, reset the clicked state (new approvals)
      if (lastPendingCount && parseInt(lastPendingCount) < currentPendingCount) {
        setNotificationClicked(false)
        localStorage.removeItem('notification_viewed')
      } else {
        // Check if notification was clicked before for the same count
        const notificationViewed = localStorage.getItem('notification_viewed')
        if (notificationViewed === 'true' && lastPendingCount === String(currentPendingCount)) {
          setNotificationClicked(true)
        }
      }
      
      // Update localStorage with current count
      localStorage.setItem('last_pending_approvals_count', String(currentPendingCount))
    }
  }, [dashboardData])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/admin/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDashboardData(data)
      } else {
        setError('Failed to load dashboard data')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    history.push('/login')
  }

  if (loading) {
    return <LoadingScreen message="Loading Admin Dashboard" />
  }

  if (error) {
    return (
      <div className="admin-dashboard-container">
        <div className="error">{error}</div>
      </div>
    )
  }

  return (
    <div className="admin-dashboard-container">
      <header className="admin-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <div className="user-info">
            {dashboardData?.statistics?.total_pending_approvals > 0 && (
              <div 
                className={`notification-badge ${notificationClicked ? 'notification-viewed' : ''}`}
                title={`${dashboardData.statistics.total_pending_approvals} user(s) pending approval`}
                onClick={() => {
                  setNotificationClicked(true)
                  localStorage.setItem('notification_viewed', 'true')
                  localStorage.setItem('last_pending_approvals_count', String(dashboardData.statistics.total_pending_approvals))
                  history.push('/admin/users')
                }}
                style={{ cursor: 'pointer' }}
              >
                <span className="notification-icon">🔔</span>
                <span className="notification-count" style={{ display: 'inline-flex' }}>
                  {dashboardData.statistics.total_pending_approvals}
                </span>
              </div>
            )}
            <span className="welcome-text">Welcome, {user?.full_name}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.total_users || 0}</h3>
              <p>Total Users</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🎓</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.total_students || 0}</h3>
              <p>Students</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.total_tests || 0}</h3>
              <p>Total Tests</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.completed_payments || 0}</h3>
              <p>Completed Payments</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">👨‍🏫</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.total_teachers || 0}</h3>
              <p>Teachers</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">👁️</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.total_invigilators || 0}</h3>
              <p>Invigilators</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🤝</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.total_volunteers || 0}</h3>
              <p>Volunteers</p>
            </div>
          </div>
        </div>

        <div className="admin-sections">
          <section className="recent-activity">
            <h2>Recent Test Attempts</h2>
            <div className="activity-list">
              {dashboardData?.recent_attempts && dashboardData.recent_attempts.length > 0 ? (
                dashboardData.recent_attempts.map((attempt) => (
                  <div key={attempt.id} className="activity-item">
                    <div className="activity-info">
                      <h4>{attempt.student_name}</h4>
                      <p>{attempt.test_title}</p>
                      <p>Score: {attempt.percentage}%</p>
                    </div>
                    <div className="activity-date">
                      {new Date(attempt.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state-message">
                  <div className="empty-icon">📊</div>
                  <p>No recent test attempts</p>
                  <span className="empty-hint">Test attempts will appear here once students start taking tests</span>
                </div>
              )}
            </div>
          </section>

          <section className="offline-registrations">
            <h2>Offline Registrations</h2>
            <div className="registrations-list">
              {dashboardData?.offline_registrations && dashboardData.offline_registrations.length > 0 ? (
                dashboardData.offline_registrations.map((reg) => (
                  <div key={reg.id} className="registration-item">
                    <div className="reg-info">
                      <h4>{reg.student_name}</h4>
                      <p>{reg.test_title}</p>
                      <p>Roll: {reg.roll_number}</p>
                    </div>
                    <div className="reg-status">
                      <span className={`status-badge ${reg.payment_status}`}>
                        {reg.payment_status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state-message">
                  <div className="empty-icon">📝</div>
                  <p>No offline registrations</p>
                  <span className="empty-hint">Offline registrations will appear here when created</span>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="admin-actions">
          <button 
            className="primary-button"
            onClick={() => history.push('/admin/tests')}
          >
            Manage Tests
          </button>
          <button 
            className="secondary-button"
            onClick={() => history.push('/admin/users')}
          >
            Manage Users
          </button>
          <button 
            className="secondary-button"
            onClick={() => history.push('/admin/payments')}
          >
            View Payments
          </button>
        </div>
      </main>
    </div>
  )
}

export default AdminDashboard
